import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { ReactElement } from 'react'
import type { Dashboard, MonatsReihe } from '../../../api'
import { api } from '../../../api'
import { Betrag } from '../../../components/Betrag'
import { Tabelle } from '../../../components/Tabelle'
import { Verweis } from '../../../components/Verweis'
import { Zustand } from '../../../components/Zustand'
import { pruefKind } from '../../../pruefkinds'
import type { SeitenProps } from '../../../screen'

export interface DashboardSuche {
  jahr?: number
}

/**
 * Auf welches Datum jeder Topf gebucht wird — Spezifikation §1.9, wörtlich, und auf dem Bildschirm sichtbar.
 * Genau hier hat das Sheet zwei Daten verwechselt: „Abschluss am“ trägt das Auftragsvolumen, „Bezahlt am“ den
 * Cashflow, das Wirkdatum der Rate die fälligen Provisionen. Wer das nicht weiß, sucht die Differenz im Betrag.
 */
const STICHTAG = {
  auftragsvolumen:
    'Gebucht auf den Monat von „Abschluss am“. Abschlüsse ohne dieses Datum stehen in „ohne Datum“, nicht im Nichts. Ohne Status Storno und Ignorieren.',
  cashflow: 'Gebucht auf den Monat von „Bezahlt am“ der einzelnen Zahlungsrate.',
  netto: 'Gebucht auf den Monat von „Bezahlt am“ derselben Rate; Basis ist der Nettoanteil dieser Rate.',
  faellig: 'Gebucht auf das Wirkdatum der Zahlungsrate; manuelle Korrekturen auf ihren Anlagetag (Berliner Datum).',
  ausgezahlt: 'Gebucht auf die Wertstellung der Auszahlung, nicht auf das Rechnungsdatum.',
  offen: 'Je Monatstopf: fällig minus ausgezahlt.',
} as const

interface KennzahlProps {
  titel: string
  cents: number
  stichtag: string
  zusatz?: string
}

/**
 * Eine Zeile unter einem Haarstrich, keine Karte: der Vertrag verbietet das Kartenraster
 * ausdrücklich („rules instead of cards … no rounded-corner card grids“). Beschriftung links,
 * Betrag rechts, Zusatz und Stichtag darunter in --ink-muted. `role="group"` mit `aria-label`,
 * damit eine Vorlesesoftware Beschriftung und Zahl als eine Einheit ansagt.
 */
function Kennzahl({ titel, cents, stichtag, zusatz }: KennzahlProps): ReactElement {
  return (
    <div role="group" aria-label={titel}>
      <dt>{titel}</dt>
      <dd>
        <Betrag cents={cents} />
      </dd>
      {zusatz === undefined ? null : <dd className="leise">{zusatz}</dd>}
      <dd className="leise">{stichtag}</dd>
    </div>
  )
}

/** Die Monatsschlüssel aller vier Reihen, ohne Dopplung, aufsteigend. Zusammenführen ist kein Summieren. */
function monatsschluessel(daten: Dashboard): string[] {
  const alle = new Set<string>()
  for (const reihe of [daten.auftragsvolumen, daten.cashflow_brutto, daten.netto_basis]) {
    for (const m of reihe.monate) alle.add(m.monat)
  }
  for (const m of daten.provisionen.monate) alle.add(m.monat)
  return [...alle].sort()
}

function centsIm(reihe: MonatsReihe, monat: string): number | null {
  return reihe.monate.find((m) => m.monat === monat)?.cents ?? null
}

function Zelle({ cents }: { cents: number | null }): ReactElement {
  return <td className="zahl">{cents === null ? '—' : <Betrag cents={cents} />}</td>
}

export function DashboardSeite({ tenantId, suche, setzeSuche, gehZu }: SeitenProps<DashboardSuche>): ReactElement {
  // Dieselbe Maskierung wie für `kind` weiter unten: ein Pfadabschnitt aus einem Route-Parameter geht
  // nirgends ungeprüft in eine Adresse.
  const tid = encodeURIComponent(tenantId)
  // Ohne `jahr` in der URL wird `year` bewusst NICHT mitgeschickt: der Server setzt dann das Berliner
  // Jahr (routes/tenant.ts, todayBerlin()). Nähme der Client hier `new Date().getFullYear()`, sähe ein
  // Betrachter in einer anderen Zeitzone um den Jahreswechsel ein anderes Jahr als der Server gewählt
  // hätte — in einem System, das sonst überall auf dem Berliner Datum besteht.
  const abfrage = useQuery({
    queryKey: ['dashboard', tenantId, suche.jahr ?? null],
    queryFn: () =>
      api.get<Dashboard>(`/api/t/${tid}/dashboard`, suche.jahr === undefined ? undefined : { year: suche.jahr }),
  })

  if (abfrage.isPending) return <Zustand art="laedt" beschriftung="Die Kennzahlen werden geladen." />
  if (abfrage.isError) {
    return (
      <Zustand
        art="fehler"
        fehler={abfrage.error}
        onWiederholen={() => {
          void abfrage.refetch()
        }}
      />
    )
  }

  const daten = abfrage.data
  const monate = monatsschluessel(daten)
  // Die Jahresauswahl steht auf dem Jahr, das der Server geantwortet hat — auch dann, wenn keines in
  // der URL stand und er das Berliner Jahr eingesetzt hat.
  const jahr = daten.jahr

  return (
    <div className="seite">
      <h1>Dashboard {daten.jahr}</h1>

      <nav className="jahre" aria-label="Jahr wählen">
        {[jahr - 1, jahr, jahr + 1].map((j) => (
          <button
            key={j}
            type="button"
            aria-current={j === jahr ? 'true' : undefined}
            onClick={() => {
              setzeSuche({ jahr: j })
            }}
          >
            Jahr {j}
          </button>
        ))}
      </nav>

      <dl className="kennzahlen">
        <Kennzahl
          titel={`Auftragsvolumen ${daten.jahr}`}
          cents={daten.auftragsvolumen.gesamt_cents}
          zusatz="einschließlich der Abschlüsse ohne Abschlussdatum"
          stichtag={STICHTAG.auftragsvolumen}
        />
        <Kennzahl
          titel={`Cashflow brutto ${daten.jahr}`}
          cents={daten.cashflow_brutto.gesamt_cents}
          stichtag={STICHTAG.cashflow}
        />
        <Kennzahl
          titel={`Netto-Basis ${daten.jahr}`}
          cents={daten.netto_basis.gesamt_cents}
          stichtag={STICHTAG.netto}
        />
        <Kennzahl titel="Provisionen fällig" cents={daten.provisionen.faellig_cents} stichtag={STICHTAG.faellig} />
        <Kennzahl
          titel="Provisionen ausgezahlt"
          cents={daten.provisionen.ausgezahlt_cents}
          stichtag={STICHTAG.ausgezahlt}
        />
        <Kennzahl titel="Provisionen offen" cents={daten.provisionen.offen_cents} stichtag={STICHTAG.offen} />
      </dl>

      <p className="hinweis" data-testid="saldo-hinweis">
        Auftragsvolumen, Cashflow und Netto-Basis zählen {daten.jahr} (Auftragsvolumen zuzüglich der Abschlüsse
        ohne Abschlussdatum). Fällig, ausgezahlt und offen sind der Gesamtsaldo über alle Jahre — dieselbe
        Abfrage, mit der die nächtliche Prüfung die Salden kontrolliert. Die Monatszeilen unten zeigen
        ausschließlich {daten.jahr}.
      </p>

      <h2>Provisionen je Empfänger</h2>
      <Tabelle
        beschriftung="Provisionen je Empfänger, Gesamtsaldo über alle Jahre"
        kopf={
          <tr>
            <th scope="col">Kürzel</th>
            <th scope="col">Empfänger</th>
            <th scope="col" className="zahl">
              Fällig
            </th>
            <th scope="col" className="zahl">
              Ausgezahlt
            </th>
            <th scope="col" className="zahl">
              Offen
            </th>
          </tr>
        }
      >
        {daten.provisionen.empfaenger.map((e) => (
          <tr key={e.recipient_tc_id}>
            <td className="mono">{e.short_code}</td>
            <th scope="row">{e.display_name}</th>
            <Zelle cents={e.faellig_cents} />
            <Zelle cents={e.ausgezahlt_cents} />
            <Zelle cents={e.offen_cents} />
          </tr>
        ))}
      </Tabelle>

      <h2>Monatsverlauf {daten.jahr}</h2>
      <Tabelle
        beschriftung={`Monatsverlauf ${daten.jahr}`}
        kopf={
          <tr>
            <th scope="col">Monat</th>
            <th scope="col" className="zahl">
              Auftragsvolumen
            </th>
            <th scope="col" className="zahl">
              Cashflow brutto
            </th>
            <th scope="col" className="zahl">
              Netto-Basis
            </th>
            <th scope="col" className="zahl">
              Provisionen fällig
            </th>
            <th scope="col" className="zahl">
              Ausgezahlt
            </th>
            <th scope="col" className="zahl">
              Offen
            </th>
          </tr>
        }
      >
        {monate.map((monat) => {
          const prov = daten.provisionen.monate.find((m) => m.monat === monat)
          return (
            <tr key={monat}>
              <th scope="row" className="mono">
                {monat}
              </th>
              <Zelle cents={centsIm(daten.auftragsvolumen, monat)} />
              <Zelle cents={centsIm(daten.cashflow_brutto, monat)} />
              <Zelle cents={centsIm(daten.netto_basis, monat)} />
              <Zelle cents={prov?.faellig_cents ?? null} />
              <Zelle cents={prov?.ausgezahlt_cents ?? null} />
              <Zelle cents={prov?.offen_cents ?? null} />
            </tr>
          )
        })}
        <tr>
          <th scope="row">ohne Datum</th>
          <Zelle cents={daten.auftragsvolumen.ohne_datum_cents} />
          <Zelle cents={daten.cashflow_brutto.ohne_datum_cents} />
          <Zelle cents={daten.netto_basis.ohne_datum_cents} />
          <Zelle cents={null} />
          <Zelle cents={null} />
          <Zelle cents={null} />
        </tr>
      </Tabelle>
      <p className="hinweis">
        „ohne Datum“ bei Cashflow und Netto-Basis sind Raten ohne „Bezahlt am“ — dieselben Fälle, die in der
        Prüfliste unter „Bezahlt am fehlt“ stehen.
      </p>

      <h2>Kontrolle</h2>
      {daten.kontrolle.length === 0 ? (
        <p>Nichts zu prüfen.</p>
      ) : (
        <ul className="kontrolle">
          {daten.kontrolle.map((zaehler) => (
            <li key={zaehler.kind}>
              <Verweis href={`/t/${tid}/pruefen?art=${encodeURIComponent(zaehler.kind)}`} gehZu={gehZu}>
                {pruefKind(zaehler.kind).titel}: {zaehler.anzahl}
              </Verweis>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export const Route = createFileRoute('/t/$tenantId/dashboard')({
  validateSearch: (roh: Record<string, unknown>): DashboardSuche => {
    const jahr = Number(roh['jahr'])
    return Number.isInteger(jahr) && jahr >= 2000 && jahr <= 2100 ? { jahr } : {}
  },
  component: DashboardRoute,
})

function DashboardRoute(): ReactElement {
  const { tenantId } = Route.useParams()
  const suche = Route.useSearch()
  const navigate = useNavigate()
  return (
    <DashboardSeite
      tenantId={tenantId}
      suche={suche}
      setzeSuche={(naechste) => {
        void navigate({ to: '.', search: (alt: DashboardSuche) => ({ ...alt, ...naechste }) })
      }}
      gehZu={(pfad) => {
        void navigate({ to: pfad })
      }}
    />
  )
}
